import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { IdpType, IdpCreate } from '@/types';
import { IDP_TYPES, getIdpProvider } from '@/types';
import { Upload, Image as ImageIcon, Check, ArrowLeft, Shield } from 'lucide-react';
import { useIdps } from '@/store/idp';

import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { InputField } from '@/components/ui/InputField';
import { InfoGrid } from '@/components/ui/InfoGrid';
import { useLayout } from '@/contexts/layout-context';

export const CreateIdpPage = () => {
    const navigate = useNavigate();
    const [provider, setProvider] = useState<IdpType>(IDP_TYPES[0]);
    const [displayName, setDisplayName] = useState<string>('');
    const [logo, setLogo] = useState<string>('');
    const [enabled, setEnabled] = useState<boolean>(true);
    const [availableTypes, setAvailableTypes] = useState<IdpType[]>([...IDP_TYPES]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const listIdpTypes = useIdps((state) => state.listIdpTypes);
    const createIdp = useIdps((state) => state.create);
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([
            { name: 'Identity Providers', to: '/idps' }, 
            { name: 'Create' }
        ]);
        listIdpTypes().then(types => {
            setAvailableTypes(types.filter(t => IDP_TYPES.includes(t as any)));
        }).catch(console.error);
    }, [setBreadcrumbs, listIdpTypes]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        const payload: IdpCreate = {
            provider,
            display_name: displayName,
            logo_url: logo || undefined,
            status: enabled ? 1 : 0,
        };
        try {
            await createIdp(payload);
            toast.success('Identity provider created successfully');
            navigate('/m/idps');
        } catch {
            toast.error('Failed to create identity provider');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 lg:p-8 max-w-5xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="Create Provider"
                subtitle="Configure a new external identity source for your tenants."
                actions={
                    <Button variant="ghost" onClick={() => navigate('/idps')} className="gap-2">
                        <ArrowLeft className="w-4 h-4" />
                        Back
                    </Button>
                }
            />

            <div className="mt-8 animate-in slide-in-from-bottom-4 duration-500">
                <Card 
                    title="Provider Configuration" 
                    subtitle="Basic settings and branding for the identity provider."
                    className="glass"
                >
                    <form onSubmit={handleSubmit} className="space-y-8">
                        <InfoGrid cols={2}>
                            <div className="space-y-2">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Provider</label>
                                <div className="flex gap-4 items-center">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden border border-slate-200/60 dark:border-slate-700/60 shadow bg-white dark:bg-slate-800 shrink-0">
                                        {getIdpProvider(provider)?.logoUrl ? (
                                            <img src={getIdpProvider(provider)?.logoUrl} alt={provider} className="w-8 h-8 object-contain" />
                                        ) : (
                                            <Shield className="w-6 h-6 text-slate-400" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <select
                                            className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition-all font-medium text-slate-900 dark:text-white"
                                            value={provider}
                                            onChange={(e) => setProvider(e.target.value as IdpType)}
                                        >
                                            {availableTypes.map((t) => (
                                                <option key={t} value={t}>{getIdpProvider(t)?.name || t}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <p className="text-[10px] text-slate-500">The architecture of the identity handshake.</p>
                            </div>
                            
                            <InputField
                                label="Display Name"
                                placeholder="e.g. Enterprise Okta"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                required
                                helpText="The name users will see when choosing their login method."
                            />

                            <div className="md:col-span-2 space-y-3">
                                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300">Branding Logo</label>
                                <div className="flex flex-col sm:flex-row items-stretch gap-4">
                                    <label className="flex-1 flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/50 hover:bg-white dark:hover:bg-slate-900 hover:border-orange-500/30 transition-all cursor-pointer group">
                                        <div className="flex flex-col items-center text-center px-4">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3 group-hover:bg-orange-100 dark:group-hover:bg-orange-500/20 group-hover:text-orange-600 transition-colors">
                                                <Upload className="w-5 h-5" />
                                            </div>
                                            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Click to upload</p>
                                            <p className="text-xs text-slate-500 mt-1">SVG, PNG, JPG (Max 800x400px)</p>
                                        </div>
                                        <input 
                                            type="file" 
                                            className="hidden" 
                                            accept="image/*"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onload = () => setLogo(reader.result as string);
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                    </label>
                                    
                                    <div className="w-40 h-40 shrink-0 border border-slate-200 dark:border-slate-800 rounded-2xl bg-white dark:bg-slate-950 flex items-center justify-center overflow-hidden shadow-inner p-2">
                                        {logo ? (
                                            <img src={logo} alt="Preview" className="max-w-full max-h-full object-contain animate-in zoom-in-95 duration-300" />
                                        ) : (
                                            <div className="text-slate-300 flex flex-col items-center gap-2">
                                                <ImageIcon className="w-8 h-8" />
                                                <span className="text-[10px] uppercase font-bold tracking-widest text-slate-400">Preview</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </InfoGrid>

                        <div className="bg-slate-50/50 dark:bg-slate-900/30 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
                            <label className="flex items-center gap-3 cursor-pointer select-none">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={enabled}
                                        onChange={(e) => setEnabled(e.target.checked)}
                                    />
                                    <div className={`block w-10 h-6 rounded-full transition-colors ${enabled ? 'bg-orange-500' : 'bg-slate-300 dark:bg-slate-700'}`}></div>
                                    <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0'}`}></div>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-bold text-slate-900 dark:text-white">Active Status</span>
                                    <span className="text-xs text-slate-500">Allow users to log in through this provider immediately.</span>
                                </div>
                            </label>
                        </div>

                        <div className="pt-8 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3">
                            <Button variant="ghost" type="button" onClick={() => navigate('/idps')}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                isLoading={isSubmitting}
                                className="min-w-40 gap-2"
                            >
                                <Check className="w-4 h-4" />
                                Create Provider
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};